import { ipayNet, newServer } from 'constants/constants';
import { PROVIDERS, ProviderOption } from 'constants/providers';

/**
 * Codigos de proveedor habilitados por el admin.
 *
 * Se consulta al backend en vez de guardarse en el frontend para que habilitar o
 * deshabilitar un proveedor no requiera desplegar el landing. Es un endpoint
 * publico (solo `x-api-key`) porque el onboarding corre antes de tener token.
 *
 * @throws si la respuesta no es 2xx
 */
export const fetchEnabledProviderCodes = async (): Promise<string[]> => {
  const response = await fetch(`${newServer}requests/providers/available`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': ipayNet.apiToken,
    },
  });

  if (!response.ok) {
    throw new Error(`providers/available respondio ${response.status}`);
  }

  const body = await response.json();
  const list = Array.isArray(body?.data) ? body.data : [];
  return list.map((item: { code: string }) => item.code);
};

/**
 * Proveedores que el onboarding debe mostrar.
 *
 * Nunca lanza: si la consulta falla se devuelve el catalogo completo
 * (*fail-open*). Un fallo transitorio del backend no debe dejar a un abogado sin
 * poder avanzar en el onboarding, y una solicitud creada con un proveedor
 * deshabilitado no se enrola sola: requiere la aprobacion de un admin, que es
 * donde el problema se detecta.
 *
 * Misma razon para devolver el catalogo completo si el backend respondiera una
 * lista vacia: es preferible ofrecer de mas que dejar el paso sin opciones.
 */
export const fetchEnabledProviders = async (): Promise<ProviderOption[]> => {
  try {
    const codes = await fetchEnabledProviderCodes();
    const enabled = PROVIDERS.filter((provider) => codes.includes(provider.code));
    return enabled.length > 0 ? enabled : PROVIDERS;
  } catch (error) {
    console.error('[providersApi] no se pudo obtener la lista habilitada', error);
    return PROVIDERS;
  }
};

/**
 * ¿El codigo sigue entre los proveedores habilitados?
 *
 * Reutiliza `fetchEnabledProviders`, asi que hereda su fail-open: si la consulta
 * falla devuelve el catalogo completo y esta funcion responde `true`. Es lo que
 * se quiere aqui: una caida transitoria del backend NO debe descartar la
 * eleccion del abogado ni hacerle repetir el paso.
 *
 * Un codigo que no este en el catalogo (por ejemplo `'both'`, que esta UI nunca
 * produce) se trata como no disponible.
 */
export const isProviderEnabled = async (code: string): Promise<boolean> => {
  const providers = await fetchEnabledProviders();
  return providers.some((provider) => provider.code === code);
};
